import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Stethoscope, Leaf, ScanLine, FileText, Factory, Handshake } from 'lucide-react';
type SectionData = {
  id: string;
  title: string;
  content: React.ReactNode;
};
const features = [{
  title: 'Created by Doctors and Scientists',
  icon: Stethoscope
}, {
  title: 'Whole Food Based Cellular Nutrition',
  icon: Leaf
}, {
  title: 'Non-GMO and Full Traceability',
  icon: ScanLine
}, {
  title: 'Clinically Proven Human Clinical Studies',
  icon: FileText
}, {
  title: 'Pharmaceutical License for Manufacturing',
  icon: Factory
}];
const traceabilitySteps = ['Meticulous Seed Selection', 'Pristine Soil & Environment', 'Field Crop Monitoring', 'Nutrient Optimizing Harvest', 'Quality Control Raw Crop Analysis', 'Nutrient Extraction & Concentration', 'Purity & Potency Verification', 'Socially Responsible Manufacturing', 'Quality Control & Laboratory Analysis', 'Product Delivery & Batch Tracking'];
export function NeoLifeQuality() {
  // State to track which sections are expanded.
  // Using a Set or array of IDs allows multiple to be open, or just one.
  // The requirement doesn't specify accordion behavior (one at a time), so independent toggles are safer.
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const toggleSection = (id: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const sections: SectionData[] = [{
    id: 'doctors',
    title: 'Created by Doctors and Scientists',
    content: <p className="text-gray-600 leading-relaxed" data-id="element-188">
          The NeoLife Scientific Advisory Board (SAB) sits at the helm of
          NeoLife’s product strategy and development, ensuring each and every
          one of our leading-edge formulas are based in nature and backed by
          science, using only the finest and safest ingredients. The SAB is made
          up of world class doctors, scientists and health experts who share our
          commitment to end the trend.
        </p>
  }, {
    id: 'cellular',
    title: 'Whole Food Based Cellular Nutrition',
    content: <p className="text-gray-600 leading-relaxed" data-id="element-189">
          For over 60 years, generations of families around the world have
          counted on NeoLife to do what no other company can do: produce
          products that are based on the finest whole food, human food chain
          ingredients and proven effective by millions of satisfied customers.
          Since we are only as healthy as our cells, we use only the most
          bio-efficient forms of nutrients to maximize nutrient absorption,
          utilization and benefit to the body.
        </p>
  }, {
    id: 'traceability',
    title: 'Non-GMO and Full Traceability',
    content: <div className="space-y-6" data-id="element-190">
          <p className="text-gray-600 leading-relaxed" data-id="element-191">
            NeoLife products use only GMO-free ingredients and are routinely
            tested for potential contaminants. NeoLife requires that suppliers
            and manufacturers guarantee that all raw materials are free from
            genetically modified DNA and genetically modified organisms. We then
            validate and closely monitor each and every ingredient in our
            laboratories and manufacturing facilities.
          </p>
          <div data-id="element-192">
            <h4 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-193">
              Full Traceability from Farm to Table
            </h4>
            <p className="text-gray-600 mb-4" data-id="element-194">
              Our state-of-the-art quality assurance program includes these 10
              steps for full traceability from farm to table.
            </p>
            <ol className="list-decimal pl-5 space-y-2 text-gray-600" data-id="element-195">
              {traceabilitySteps.map((step, index) => <li key={index} className="pl-1" data-id="element-196">
                  {step}
                </li>)}
            </ol>
          </div>
        </div>
  }, {
    id: 'clinical',
    title: 'Clinically Proven Human Clinical Studies',
    content: <p className="text-gray-600 leading-relaxed" data-id="element-197">
          Human clinical studies proving the effectiveness of NeoLife products
          have been published in some of the world’s most prestigious scientific
          journals including the Journal of the American College of Nutrition,
          the International Journal of Toxicology, the FASEB Journal, and the
          American Journal of Clinical Nutrition. Human clinical studies are a
          unique point of pride for NeoLife, in a world where yeast and animal
          studies remain the predominant choice. NeoLife continues to research
          and publish studies for breakthrough supplements and organic skin
          care.
        </p>
  }, {
    id: 'pharma',
    title: 'Pharmaceutical License for Manufacturing',
    content: <p className="text-gray-600 leading-relaxed" data-id="element-198">
          NeoLife owns and operates state-of-the-art manufacturing facilities
          that surpass the most stringent global GMP standards and manufacturing
          regulations. This includes a Drug License from the State of California
          as well as the esteemed Drug License from the TGA of Australia, which
          very few manufacturers in the United States are able to obtain.
          Although we do not manufacture drugs, we hold these licenses and
          others as validation of our exceptional quality standards. We are
          registered with the FDA and operate under their regulation and
          compliance.
        </p>
  }, {
    id: 'partnerships',
    title: 'Strategic Partnerships',
    content: <p className="text-gray-600 leading-relaxed" data-id="element-199">
          NeoLife's reputation for scientific excellence is so well established
          that our products have been used by researchers from such renowned
          bodies such as the United States Department of Agriculture, UNESCO,
          and major universities and scientific institutions around the world.
          NeoLife has been able to partner with the Stanford University Health
          Library to produce the annual NeoLife-Arthur Furst Lecture Series on
          Nutrition & Disease Prevention. NeoLife is a founding member and
          remains a major contributor to the Polyphenols in Human Health
          Research Interest Group (PhenHRIG). In 2019, NeoLife was also a proud
          contributor to the 10th Linus Pauling Institute International
          Conference. For over 25 years, we have partnered with the University
          of San Francisco to award the annual Arthur Furst Undergraduate
          Scholarship. NeoLife is also a Member of the Council for Responsible
          Nutrition. Through these ongoing programs, NeoLife is changing the
          face of health care and disease prevention through nutrition by
          actively supporting today’s scientists and tomorrow’s doctors.
        </p>
  }];
  return <section className="bg-gray-50 py-16 lg:py-24 border-t border-gray-100" data-id="element-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-201">
        {/* Header Section */}
        <div className="text-center max-w-4xl mx-auto mb-16" data-id="element-202">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6" data-id="element-203">
            NeoLife Quality Difference
          </h2>
          <h3 className="text-xl text-gray-600 font-medium leading-relaxed" data-id="element-204">
            How do we create the finest, safest and most effective products in
            the world? Here’s a start…
          </h3>
        </div>

        {/* Feature Grid - Visible */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-16" data-id="element-205">
          {features.map((feature, index) => <div key={index} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col items-center text-center h-full hover:shadow-md transition-shadow duration-200" data-id="element-206">
              <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mb-4 text-[#0e7be1]" data-id="element-207">
                <feature.icon className="w-6 h-6" data-id="element-208" />
              </div>
              <span className="text-sm font-bold text-gray-900 leading-tight" data-id="element-209">
                {feature.title}
              </span>
            </div>)}
        </div>

        {/* Collapsible Sections */}
        <div className="max-w-4xl mx-auto space-y-4 mb-20" data-id="element-210">
          {sections.map(section => <div key={section.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm" data-id="element-211">
              <button onClick={() => toggleSection(section.id)} className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-gray-50 transition-colors duration-200 focus:outline-none" aria-expanded={expandedSections[section.id]} data-id="element-212">
                <span className="text-lg font-bold text-gray-900" data-id="element-213">
                  {section.title}
                </span>
                {expandedSections[section.id] ? <ChevronUp className="w-5 h-5 text-[#0e7be1] flex-shrink-0 ml-4" data-id="element-214" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0 ml-4" data-id="element-215" />}
              </button>

              <div className={`transition-all duration-300 ease-in-out ${expandedSections[section.id] ? 'max-h-[2000px] opacity-100 border-t border-gray-100' : 'max-h-0 opacity-0 overflow-hidden'}`} data-id="element-216">
                <div className="p-6 pt-4 bg-white" data-id="element-217">{section.content}</div>
              </div>
            </div>)}
        </div>

        {/* Video Section */}
        <div className="max-w-4xl mx-auto" data-id="element-218">
          <div className="relative w-full aspect-video rounded-xl overflow-hidden shadow-lg bg-black" data-id="element-219">
            <iframe className="absolute top-0 left-0 w-full h-full" src="https://www.youtube-nocookie.com/embed/f8DdV_Gyfsg?rel=0" title="NeoLife Quality Video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-220"></iframe>
          </div>
        </div>
      </div>
    </section>;
}